import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { useNotifications, useSubmitOrder, useVendors, safeMessage } from '@/lib/queries';
import { useNow } from '@/lib/use-now';
import { ChefNav } from '@/components/ChefNav';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { locationLabel } from '@/lib/locations';
import { formatCountdown, formatDeliveryDay, formatQuantity, formatTime } from '@/lib/utils';
import type { CartLine } from '@/lib/cart';

// Order Review & Submit — confirmation of the draft order before submission,
// one section per vendor with its delivery day and order deadline.
export function OrderReview() {
  const { user } = useAuth();
  const { lines, clear } = useCart();
  const { data: notifications } = useNotifications();
  const submitOrder = useSubmitOrder();
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const items = [...lines.values()].filter((l) => l.quantity > 0);
  const locationId = user?.locationIds[0];
  const { data: vendorData } = useVendors(locationId);
  const now = useNow();
  const byVendor = new Map<string, CartLine[]>();
  for (const l of items) byVendor.set(l.supplierId, [...(byVendor.get(l.supplierId) ?? []), l]);
  const totalItems = items.reduce((sum, l) => sum + l.quantity, 0);

  async function handleSubmit() {
    if (!locationId || items.length === 0) {
      setError('Your cart is empty');
      return;
    }
    setError(null);
    try {
      const result = await submitOrder.mutateAsync({
        locationId,
        notes: notes || undefined,
        lines: items.map((l) => ({ productId: l.productId, formatId: l.formatId, quantity: l.quantity })),
      });
      clear();
      navigate(`/app/orders/${result.data.orderId}/confirmation`);
    } catch (err) {
      setError(safeMessage(err));
    }
  }

  return (
    <div className="min-h-screen bg-canvas">
      <ChefNav notifications={notifications?.data ?? []} />
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        <h1 className="page-title">Review Order</h1>
        <p className="page-sub">Confirm your order before it is routed to suppliers.</p>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="card p-6 lg:col-span-2">
            <h2 className="section-head mb-4">Line Items</h2>
            {items.length === 0 ? (
              <p className="text-muted">No items selected.</p>
            ) : (
              <div className="space-y-6">
                {[...byVendor].map(([supplierId, vendorLines]) => {
                  const vendor = vendorData?.data.find((v) => v.id === supplierId);
                  const left = vendor?.orderBy ? Date.parse(vendor.orderBy) - now : null;
                  return (
                    <section key={supplierId}>
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-line pb-2">
                        <h3 className="font-head text-base font-extrabold uppercase tracking-wide text-brand">
                          {vendor?.name ?? 'Vendor'}
                        </h3>
                        {vendor?.orderBy && left !== null && (
                          <span className={left < 2 * 3_600_000 ? 'text-xs font-semibold text-amber' : 'text-xs text-muted'}>
                            Delivery {formatDeliveryDay(vendor.deliveryDate)} · order by {formatTime(vendor.orderBy)} (
                            {formatCountdown(left)} left)
                          </span>
                        )}
                      </div>
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-b border-line text-muted">
                            <th className="py-2 pr-4 font-medium">Item</th>
                            <th className="py-2 pr-4 font-medium">Quantity</th>
                            <th className="py-2 pr-4 font-medium">Format</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendorLines.map((l) => (
                            <tr key={l.productId} className="border-b border-line last:border-b-0">
                              <td className="py-3 pr-4 text-ink">{l.name}</td>
                              <td className="py-3 pr-4 text-ink">{formatQuantity(l.quantity)}</td>
                              <td className="py-3 pr-4 text-muted">{l.formatLabel ?? l.unitOfMeasure}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="section-head mb-4">Order Summary</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="eyebrow">Location</dt>
                <dd className="font-head text-base font-extrabold uppercase text-brand">
                  {locationLabel(locationId)}
                </dd>
              </div>
              <div>
                <dt className="eyebrow">Order Date</dt>
                <dd className="text-ink">{new Date().toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="eyebrow">Vendors</dt>
                <dd className="text-ink">{byVendor.size}</dd>
              </div>
              <div>
                <dt className="eyebrow">Total Items</dt>
                <dd className="text-ink">{items.length} lines</dd>
              </div>
              <div>
                <dt className="eyebrow">Total Units</dt>
                <dd className="text-ink">{formatQuantity(totalItems)}</dd>
              </div>
            </dl>

            <label className="mt-5 block">
              <span className="eyebrow mb-1 block">Notes / Special Instructions</span>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
            </label>

            {error && <p className="mt-3 text-sm text-danger">{error}</p>}

            <div className="mt-6 space-y-3">
              <Button className="w-full" onClick={handleSubmit} disabled={submitOrder.isPending || items.length === 0}>
                {submitOrder.isPending ? 'Placing Order…' : 'Place Order'}
              </Button>
              <Link to="/app" className="block text-center text-sm text-brand hover:underline">
                Back to Catalog
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
